// node_modules
import {
    FC, useCallback, useContext, useEffect, useRef, useState
} from 'react';
import { faArrowDown, faArrowUp, faTimes, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// Types
import { TTechnologyHighlightDTO, TTechnologyHighlightsGroupDTO } from 'Types';
// Components
import { EditableMarkdown } from 'Components/Shared';
import { CreateTechnologyHighlightsGroup } from '../CreateTechnologyHighlightsGroup/CreateTechnologyHighlightsGroup';
// Styles
import styles from "./editableTechnologyHighlight.module.scss";
// Controllers
import { TechnologyHighlightsControllerSingleton } from 'Controllers';
// Custom hooks
import { useClickOutsideRef, useDebounce } from 'Hooks';
// Constants
import { TechnologyHighlightsConstants } from 'Constants';
// Contexts
import { CaseContext } from 'Stores';

type TEditableTechnologyHighlightProps = {
    technologyHighlightsGroup: TTechnologyHighlightsGroupDTO,
    highlight: TTechnologyHighlightDTO,
    numbering: number,
    allTechnologyHighlightsGroup: TTechnologyHighlightsGroupDTO[],
    caseId: number,
    technologyId: number,
    highlightsGroupResourceName?: string,
    highlightDescriptionResourceName?: string,
    setNewSelectedGroupId: (newSelectedGroupId: number) => void
}

export const EditableTechnologyHighlight: FC<TEditableTechnologyHighlightProps> = ({ technologyHighlightsGroup, numbering, highlight, allTechnologyHighlightsGroup, caseId, technologyId, setNewSelectedGroupId, highlightsGroupResourceName, highlightDescriptionResourceName }: TEditableTechnologyHighlightProps) => {
    // Ref
    const swappableTechnologyHighlightsRef = useRef<HTMLDivElement>(null);

    // State
    const [highlightDescriptionEdited, setHighlightDescriptionEdited] = useState<string | undefined>(undefined);
    const [areSwappableTechnologyHighlightsShown, setAreSwappableTechnologyHighlightsShown] = useState<boolean>(false);

    // Contexts
    const { caseState } = useContext(CaseContext);
    
    // Let
    const debouncedValue = useDebounce(highlightDescriptionEdited, 500);

    // Logic
    const handleClickOutsideRef = (): void => {
        setAreSwappableTechnologyHighlightsShown(false);
    };

    useClickOutsideRef(swappableTechnologyHighlightsRef, handleClickOutsideRef);

    const editTechnologyHighlightDescription = useCallback(
        async (text: string, highlightId: number) => {
            // safety-checks
            if (!caseState.selectedTechnologyId || text === null || text === undefined || !highlightId
                || highlightDescriptionEdited === null || highlightDescriptionEdited === undefined) return;

            setHighlightDescriptionEdited(undefined);

            await TechnologyHighlightsControllerSingleton
                .editTechnologyHighlightDescriptionAsync(caseId, highlightId, text, caseState.selectedTechnologyId);
        }, [caseId, caseState.selectedTechnologyId, highlightDescriptionEdited]);

    useEffect(() => {
        // do not trigger edit at the first render
        if (debouncedValue === null || debouncedValue === undefined) return;
        // send new description to the server
        editTechnologyHighlightDescription(debouncedValue, highlight.id);
    }, [highlight.id, editTechnologyHighlightDescription, debouncedValue]);

    const increaseSignificanceOfTechnologyHighlight = async (highlightId: number): Promise<void> => {
        if (!highlightsGroupResourceName) return;

        await TechnologyHighlightsControllerSingleton
            .increaseSignificanceOfTechnologyHighlightAsync(caseId, technologyId, highlightId, technologyHighlightsGroup.id);
    };

    const decreaseSignificanceOfTechnologyHighlight = async (highlightId: number): Promise<void> => {
        if (!highlightsGroupResourceName) return;

        await TechnologyHighlightsControllerSingleton
            .decreaseSignificanceOfTechnologyHighlightAsync(caseId, technologyId, highlightId, technologyHighlightsGroup.id);
    };

    const deleteTechnologyHighlight = async (highlightId: number): Promise<void> => {
        if (!highlightsGroupResourceName) return;

        await TechnologyHighlightsControllerSingleton
            .deleteTechnologyHighlightAsync(caseId, technologyId, highlightId);
    };

    const removeHighlightFromTechnologyHighlightGroup = async (highlightId: number, groupId: number): Promise<void> => {
        if (!highlightsGroupResourceName || !caseState.selectedTechnologyId) return;

        await TechnologyHighlightsControllerSingleton
            .removeTechnologyHighlightFromTechnologyHighlightsGroupAsync(caseId, technologyId, highlightId, groupId);
    };

    const handleTechnologyHighlightsGroupCreated = async (newTechnologyHighlightsGroup: TTechnologyHighlightsGroupDTO, highlightId: number): Promise<void> => {
        if (!highlightsGroupResourceName || !caseState.selectedTechnologyId) return;

        await TechnologyHighlightsControllerSingleton
            .addTechnologyHighlightToTechnologyHighlightsGroupAsync(caseId, technologyId, highlightId, newTechnologyHighlightsGroup.id);
    };

    const swapTwoTechnologyHighlights = async (technologyHighlightIdA: number, technologyHighlightIdB: number): Promise<void> => {
        await TechnologyHighlightsControllerSingleton
            .swapTwoTechnologyHighlightsAsync(caseId, technologyId, technologyHighlightIdA, technologyHighlightIdB, technologyHighlightsGroup.id);
    };

    const getGroupsOfTechnologyHighlight = (highlightId: number): TTechnologyHighlightsGroupDTO[] => {
        return allTechnologyHighlightsGroup
            .filter(group =>
                group
                    .highlights
                    .filter(highlight => highlight.id === highlightId).length === 1 &&
                group.id !== TechnologyHighlightsConstants.ALL_TECHNOLOGY_HIGHLIGHTS_GROUP_ID &&
                group.name.toLowerCase() !== TechnologyHighlightsConstants.ALL_TECHNOLOGY_HIGHLIGHTS_GROUP_NAME.toLowerCase());
    };

    const getAddableTechnologyHighlightsGroup = (): TTechnologyHighlightsGroupDTO[] => {
        return allTechnologyHighlightsGroup
            .filter(group =>
                group
                    .highlights
                    .filter(hl => hl.id === highlight.id).length === 0 &&
                group.id !== TechnologyHighlightsConstants.ALL_TECHNOLOGY_HIGHLIGHTS_GROUP_ID &&
                group.name.toLowerCase() !== TechnologyHighlightsConstants.ALL_TECHNOLOGY_HIGHLIGHTS_GROUP_NAME.toLowerCase());
    };

    const getSwappableTechnologyHighlights = (): JSX.Element[] | null => {
        let swappableTechnologyHighlights: {
            highlightId: number;
            numbering: number;
        }[] = [];

        let numbering: number = 0;

        for (const hl of technologyHighlightsGroup.highlights) {
            numbering++;
            if (hl.id !== highlight.id) {
                swappableTechnologyHighlights.push({ highlightId: hl.id, numbering });
            }
        }


        return swappableTechnologyHighlights.map((swappableTechnologyHighlight) => {
            return (
                <div className={styles.moveHighlightByNumberOption} key={swappableTechnologyHighlight.highlightId} onClick={() => swapTwoTechnologyHighlights(highlight.id, swappableTechnologyHighlight.highlightId)}>{swappableTechnologyHighlight.numbering}</div>
            );
        });
    };

    return (
        <div className={styles.editableHighlightContainer}>
            <div className={styles.editableHighlightActionContainer}>
                {technologyHighlightsGroup.id !== TechnologyHighlightsConstants.ALL_TECHNOLOGY_HIGHLIGHTS_GROUP_ID &&
                    technologyHighlightsGroup.name.toLowerCase() !== TechnologyHighlightsConstants.ALL_TECHNOLOGY_HIGHLIGHTS_GROUP_NAME.toLowerCase() &&
                    numbering !== 1 ?
                    <div className={styles.moveHighlightUp} title={"Move highlight up"} onClick={() => increaseSignificanceOfTechnologyHighlight(highlight.id)} >
                        <FontAwesomeIcon icon={faArrowUp} />
                    </div>
                    :
                    null
                }
                {technologyHighlightsGroup.id !== TechnologyHighlightsConstants.ALL_TECHNOLOGY_HIGHLIGHTS_GROUP_ID &&
                    technologyHighlightsGroup.name.toLowerCase() !== TechnologyHighlightsConstants.ALL_TECHNOLOGY_HIGHLIGHTS_GROUP_NAME.toLowerCase() ?
                    <div className={styles.moveHighlightByNumberContainer}>
                        <div className={styles.moveHighlightByNumberCurrent} title={"Move highlihgt by number"} onClick={() => setAreSwappableTechnologyHighlightsShown(true)}>{numbering}</div>
                        {areSwappableTechnologyHighlightsShown ?
                            <div className={styles.moveHighlightByNumberOptionsContainer} ref={swappableTechnologyHighlightsRef}>
                                {getSwappableTechnologyHighlights()}
                            </div>
                            :
                            null
                        }
                    </div>
                    :
                    null
                }
                {technologyHighlightsGroup.id !== TechnologyHighlightsConstants.ALL_TECHNOLOGY_HIGHLIGHTS_GROUP_ID &&
                    technologyHighlightsGroup.name.toLowerCase() !== TechnologyHighlightsConstants.ALL_TECHNOLOGY_HIGHLIGHTS_GROUP_NAME.toLowerCase() &&
                    numbering !== technologyHighlightsGroup.highlights.length ?
                    <div className={styles.moveHighlightDown} title={"Move highlight down"} onClick={() => decreaseSignificanceOfTechnologyHighlight(highlight.id)} >
                        <FontAwesomeIcon icon={faArrowDown} />
                    </div>
                    :
                    null
                }
                <div className={styles.deleteHighlight} title={"Delete highlight"} onClick={() => deleteTechnologyHighlight(highlight.id)} >
                        <FontAwesomeIcon icon={faTrashAlt} />
                </div>
            </div>
            <div className={styles.editableHighlightContentContainer}>
                <div className={styles.editableHighlightContentHeaderContainer}>
                    <div className={styles.editableHighlightGroupsContainer}>
                        {getGroupsOfTechnologyHighlight(highlight.id).map((group) => {
                            return (
                                <div className={styles.editableHighlightGroup} key={group.id}>
                                    <span>{group.name}</span>
                                    <div style={{display: "flex"}} title={"Delete group from highlight"}>
                                        <FontAwesomeIcon
                                            icon={faTimes}
                                            onClick={() => removeHighlightFromTechnologyHighlightGroup(highlight.id, group.id)} />
                                    </div>
                                </div>
                            )
                        })}
                        <CreateTechnologyHighlightsGroup
                            setNewSelectedGroupId={setNewSelectedGroupId}
                            technologyHighlightsGroups={getAddableTechnologyHighlightsGroup()}
                            addTechnologyHighlightToTechnologyHighlightGroup={handleTechnologyHighlightsGroupCreated}
                            highlightId={highlight.id}
                            isInHighlightSection={true} />
                    </div>
                </div>
                <EditableMarkdown 
                    source={!highlight.description ? "" : highlight.description} 
                    entityId={highlight.id.toString()}
                    isEditable={true} 
                    handleSourceEdited={(text: string) => setHighlightDescriptionEdited(text)} 
                    env={technologyId ? {"technologyId": technologyId} : undefined}
                    resourceName={highlightDescriptionResourceName} />
            </div>
        </div>
    )
};
